/* eslint-disable */
import React, { useContext, useState, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import TwoStepMultiSelectDropdown from 'components/2StepMultiSelectionDropdown'
import TwoStepMultiSelectLabel from 'components/2StepMultiSelectionDropdown/components/Label'
import Select from 'components/Select/selectAbout'
import Textarea from 'components/Textarea'
import UploadSiteFiles from 'components/SitesUploads'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import {
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_SUPERSEDED_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { delay } from 'utils/delay'
import ScrollToTop from 'components/ScrollToTop'
import { SiteContext } from 'providers/site'
import {
  requestUpdateEditOnlySite,
  requestUpdateSite,
  requestGetSiteFiles,
  requestUpdateSiteFiles,
  requestDeleteSiteFiles,
  requestSubmitSensitiveSpeciesAcknowledgment
} from 'api/site'
import Uploader from './uploader'
import addGraphicsToKBALayer from 'utils/addGraphicsToKBALayer'
import { requestGetCurrentSiteGeometry, requestGetDraftSiteGeometry } from 'api/site'
import Graphic from '@arcgis/core/Graphic'
import { PageTitle, PageSubtitle, TooltipHintWrapper, PageSubtitleWrapper } from './style'
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID } from 'constants/roles'
import { canViewSensitiveSpeciesAtSiteFullAccess } from 'utils/rolePermissions'
import { isSpeciesSensitive, parseSensitiveFlag, SPECIES_SENSITIVE_KEYS } from 'utils/sensitiveSpecies'
import { ProfileContext } from 'providers/profile'
import SensitiveSpeciesWarningModal from 'components/Modal/SensitiveSpeciesWarningModal'
import { showSuccessToast, showErrorToast } from 'utils/toast'

let reviewesrSavetimeout = null

const tooltipXOffset = 0
const tooltipYOffset = 16
const inputSaveDelayTime = 300
const biodiversityKnowledgeHoldersSavetimeout = delay(inputSaveDelayTime)
const previousProposersSavetimeout = delay(inputSaveDelayTime)
const governmentSavetimeout = delay(inputSaveDelayTime)
const customaryLegalRightsHoldersSavetimeout = delay(inputSaveDelayTime)

const proposalPurposeOptions = [
  {
    label: 'Propose a new KBA that does not intersect any existing KBAs',
    value: 1,
    disabled: false
  },
  { label: 'Propose a new KBA that replaces one or more existing KBA(s)', value: 2, disabled: false },
  {
    label:
      'Re-assess an existing KBA: re-assess existing biodiversity elements (species, ecosystems etc) meeting KBA criteria (including delisting of any species that no longer meet criteria)',
    value: 3,
    disabled: false
  },
  {
    label:
      'Re-assess an existing KBA: propose additional biodiversity elements (species, ecosystems etc) meeting KBA criteria',
    value: 4,
    disabled: false
  },
  {
    label: 'Re-assess an existing KBA:  re-assess existing biodiversity elements AND propose additional elements',
    value: 5,
    disabled: false
  },
  {
    label: 'Re-assess an existing KBA: disqualify all existing biodiversity elements and delist the KBA',
    value: 6,
    disabled: false
  },
  {
    label:
      'Edit only:Make revisions to the boundary of an existing KBA to improve its accuracy (relative to the biodiversity elements meeting criteria and to the assessment period)',
    value: 7,
    disabled: false
  },
  {
    label: 'Edit only: Make minor amendments to the details of an existing assessment',
    value: 8,
    disabled: false
  }
]

const REASSESSMENT_PURPOSE_VALUES = [3, 4, 5, 6]

const shouldShowSensitiveWarning = (
  proposalPurposeValue,
  isIndividual,
  hasHiddenSensitive,
  isNewUserWithoutAccess = false
) => {
  if (!proposalPurposeValue) {
    return false
  }

  // Show the warning only when:
  // - The user is selecting a reassessment purpose,
  // - They are an individual proposer,
  // - There are sensitive species at the site that they cannot see, and
  // - They are a new user for this site (no prior access/assessments).
  return (
    REASSESSMENT_PURPOSE_VALUES.includes(proposalPurposeValue) &&
    Boolean(isIndividual) &&
    Boolean(hasHiddenSensitive) &&
    Boolean(isNewUserWithoutAccess)
  )
}

const SiteProposalAboutPage = () => {
  const { site, isEditable, siteProposalFieldValidation, getMySite, getSiteValidation } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const params = useParams()

  const editOnlySiteStatus =
    site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = editOnlySiteStatus ? site.sitehistoryId : params.siteHistoryId

  const isReassessment = Boolean(params.siteHistoryId)

  if (!site.id) {
    return null
  }

  useEffect(() => {
    const fetchBoundaryGeometry = async () => {
      try {
        const ignoreDraftSiteGeometry = [
          SITE_STATUS_CONFIRMED_ID,
          SITE_STATUS_DELISTED_ID,
          SITE_STATUS_SUPERSEDED_ID
        ].includes(site.SiteStatusId)
        let geometryResults

        if (ignoreDraftSiteGeometry) {
          geometryResults = await requestGetCurrentSiteGeometry(params.id, historyId)
        } else {
          geometryResults = await requestGetDraftSiteGeometry(params.id, historyId)

          // If no draft geometry is found and it's a reassessment, fetch from the confirmed layer
          if (!geometryResults?.features?.length && isReassessment) {
            geometryResults = await requestGetCurrentSiteGeometry(params.id, historyId)
          }
        }

        const { features } = geometryResults
        const foundFeature = features[0]
        const geometry = {
          ...foundFeature.geometry,
          type: 'polygon',
          spatialReference: geometryResults.spatialReference
        }
        const newGraphic = new Graphic({
          geometry: geometry,
          attributes: foundFeature.attributes,
          symbol: {
            type: 'simple-fill',
            color: [150, 150, 150, 0.15],
            outline: { width: 2, color: [0, 75, 150] }
          }
        })

        if (site.SiteStatusId === SITE_STATUS_DRAFT_ID) addGraphicsToKBALayer([newGraphic], params)
      } catch {
        // Boundary fetch failed; layer will remain empty
      }
    }

    if (site.id) fetchBoundaryGeometry()
  }, [params.id, historyId, site.id, site.SiteStatusId])

  const disabledBtn =
    site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID)
  const isEditOnlyEnabledBtn =
    (site.SiteStatusId !== SITE_STATUS_DRAFT_ID && site.SiteStatusId !== SITE_STATUS_EDIT_ONLY_ID) ||
    (isEditable && (site.SiteStatusId === SITE_STATUS_DRAFT_ID || site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID)) ||
    (!isEditable &&
      site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID &&
      profile.RoleId !== INDIVIDUAL_ROLE_ID &&
      profile.RoleId !== NCG_ROLE_ID)

  const disabled = editOnlySiteStatus ? isEditOnlyEnabledBtn : disabledBtn

  const hasHiddenSensitiveSpecies = useMemo(() => {
    const candidateObjects = [
      site,
      site?.meta,
      site?.summary,
      site?.SiteSummary,
      site?.siteSummary
    ].filter(Boolean)

    const booleanKeys = ['hasHiddenSensitiveSpecies', 'has_hidden_sensitive_species']

    for (const candidate of candidateObjects) {
      for (const key of booleanKeys) {
        const value = candidate[key]
        if (typeof value === 'boolean') {
          return value
        }
      }
    }

    const countKeys = ['hiddenSensitiveSpeciesCount', 'HiddenSensitiveSpeciesCount', 'hidden_sensitive_species_count']

    for (const candidate of candidateObjects) {
      for (const key of countKeys) {
        const value = candidate[key]
        if (typeof value === 'number') {
          return value > 0
        }
      }
    }

    const arrayKeys = ['hiddenSensitiveSpecies', 'HiddenSensitiveSpecies']

    for (const candidate of candidateObjects) {
      for (const key of arrayKeys) {
        const value = candidate[key]
        if (Array.isArray(value) && value.length > 0) {
          return true
        }
        if (value?.rows && Array.isArray(value.rows) && value.rows.length > 0) {
          return true
        }
      }
    }

    // Hidden sensitive species must be communicated explicitly via metadata;
    // do not infer them from the species list.
    return false
  }, [site])

  const isIndividualProposer = profile?.RoleId === INDIVIDUAL_ROLE_ID
  const canViewSensitiveAtThisSite = canViewSensitiveSpeciesAtSiteFullAccess(site, profile?.RoleId)
  const hasHiddenSensitiveForCurrentUser = hasHiddenSensitiveSpecies && !canViewSensitiveAtThisSite

  const isNewUserWithoutAccess =
    !canViewSensitiveAtThisSite 
  

  const [threatenedBiodiversity, setThreatenedBiodiversity] = useState(site.threatenedBiodiversity || [])
  const [geographicallyRestrictedBiodiversity, setGeographicallyRestrictedBiodiversity] = useState(
    site.geographicallyRestrictedBiodiversity || []
  )
  const [ecologicalIntegrity, setEcologicalIntegrity] = useState(site.ecologicalIntegrity || false)
  const [irreplaceabilityThroughQuantitiveAnalysis, setIrreplaceabilityThroughQuantitiveAnalysis] = useState(
    site.irreplaceabilityThroughQuantitiveAnalysis || false
  )
  const [biologicalProcesses, setBiologicalProcesses] = useState(site.biologicalProcesses || [])

  const [biodiversityKnowledgeHolders, setBiodiversityKnowledgeHolders] = useState(
    site.consultationWithBiodiversityKnowledgeHolders || ''
  )
  const [previousProposers, setPreviousProposers] = useState(site.consultationWithPreviousProposers || '')
  const [government, setGovernment] = useState(site.consultationWithGovernment || '')
  const [customaryLegalRightsHolders, setCustomaryLegalRightsHolders] = useState(
    site.consultationWithCustomaryLegalRightsHolders || ''
  )
  const [reviewers, setReviewers] = useState(site.reviewersContactsThatCanComment || '')
  const [contributors_assessment, setContributorsAssessment] = useState(site.contributors_assessment || '')

  const [proposalPurpose, setProposalPurpose] = useState(
    site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID
      ? proposalPurposeOptions.find((option) => option.value === 8)
      : proposalPurposeOptions.find((option) => option.value === site.proposalPurpose) || null
  )
  const [proposalDetails, setProposalDetails] = useState(site.proposalDetails || '')
  const [isSensitiveWarningOpen, setIsSensitiveWarningOpen] = useState(false)
  const [pendingProposalPurpose, setPendingProposalPurpose] = useState(null)
  const [isConfirmingWarning, setIsConfirmingWarning] = useState(false)
  const hasShownInitialSensitiveWarningRef = useRef(false)

  // Initial-load sensitive warning: show once on mount when reassessment + conditions met.
  // For new users, proposalPurpose may not be set yet; still show the warning so they see it
  // before or when they select a reassessment purpose.
  useEffect(() => {
    if (hasShownInitialSensitiveWarningRef.current) {
      return
    }
    
    if (!isReassessment) {
      return
    }
    const currentPurpose = site?.proposalPurpose
    const purposeIsReassessment =
      currentPurpose != null && REASSESSMENT_PURPOSE_VALUES.includes(Number(currentPurpose))
    const shouldShowInitial =
      purposeIsReassessment &&
      Boolean(isIndividualProposer) &&
      Boolean(hasHiddenSensitiveForCurrentUser) &&
      Boolean(isNewUserWithoutAccess)

    if (!shouldShowInitial) {
      return
    }
    hasShownInitialSensitiveWarningRef.current = true
    if(site.SiteStatusId === SITE_STATUS_DRAFT_ID) {
      setIsSensitiveWarningOpen(true)
    }
  }, [
    isReassessment,
    site?.proposalPurpose,
    isIndividualProposer,
    hasHiddenSensitiveSpecies,
    hasHiddenSensitiveForCurrentUser,
    isNewUserWithoutAccess
  ])

  const updateThreatenedBiodiversity = async (newIds) => {
    setThreatenedBiodiversity(newIds)
    await requestUpdateSite(params.id, params.siteHistoryId, { threatenedBiodiversity: newIds })
  }

  const updateGeographicallyRestrictedBiodiversity = async (newIds) => {
    setGeographicallyRestrictedBiodiversity(newIds)
    await requestUpdateSite(params.id, params.siteHistoryId, { geographicallyRestrictedBiodiversity: newIds })
  }

  const updateEcologicalIntegrity = async (newValue) => {
    setEcologicalIntegrity(newValue)
    await requestUpdateSite(params.id, params.siteHistoryId, { ecologicalIntegrity: newValue })
  }

  const updateIrreplaceabilityThroughQuantitiveAnalysis = async (newValue) => {
    setIrreplaceabilityThroughQuantitiveAnalysis(newValue)
    await requestUpdateSite(params.id, params.siteHistoryId, { irreplaceabilityThroughQuantitiveAnalysis: newValue })
  }

  const updateBiologicalProcesses = async (newIds) => {
    setBiologicalProcesses(newIds)
    await requestUpdateSite(params.id, params.siteHistoryId, { biologicalProcesses: newIds })
  }

  const handleChangeBiodiversityKnowledgeHolders = async (e) => {
    setBiodiversityKnowledgeHolders(e.target.value)
    biodiversityKnowledgeHoldersSavetimeout(async () => {
      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID) {
        await requestUpdateEditOnlySite(site.id, {
          consultationWithBiodiversityKnowledgeHolders: e.target.value,
          proposalPurpose: 8
        })
      } else {
        await requestUpdateSite(params.id, historyId, { consultationWithBiodiversityKnowledgeHolders: e.target.value })
      }
    })
  }

  const handleChangePreviousProposers = async (e) => {
    setPreviousProposers(e.target.value)
    previousProposersSavetimeout(async () => {
      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID) {
        await requestUpdateEditOnlySite(site.id, {
          consultationWithPreviousProposers: e.target.value,
          proposalPurpose: 8
        })
      } else {
        await requestUpdateSite(params.id, historyId, { consultationWithPreviousProposers: e.target.value })
      }
    })
  }

  const handleChangeGovernment = async (e) => {
    setGovernment(e.target.value)
    governmentSavetimeout(async () => {
      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && params.siteHistoryId) {
        await requestUpdateEditOnlySite(site.id, { consultationWithGovernment: e.target.value, proposalPurpose: 8 })
      } else {
        await requestUpdateSite(params.id, historyId, { consultationWithGovernment: e.target.value })
      }
    })
  }

  const handleChangeCustomaryLegalRightsHolders = async (e) => {
    setCustomaryLegalRightsHolders(e.target.value)
    customaryLegalRightsHoldersSavetimeout(async () => {
      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && params.siteHistoryId) {
        await requestUpdateEditOnlySite(site.id, {
          consultationWithCustomaryLegalRightsHolders: e.target.value,
          proposalPurpose: 8
        })
      } else {
        await requestUpdateSite(params.id, historyId, { consultationWithCustomaryLegalRightsHolders: e.target.value })
      }
    })
  }

  const handleChangeContributorsAssessment = async (e) => {
    setContributorsAssessment(e.target.value)

    if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && params.siteHistoryId) {
      await requestUpdateEditOnlySite(site.id, {
        contributors_assessment: e.target.value,
        proposalPurpose: 8
      })
    } else {
      await requestUpdateSite(params.id, historyId, {
        contributors_assessment: e.target.value
      })
    }
  }

  const onReviewersChange = async (e) => {
    setReviewers(e.target.value)
    clearTimeout(reviewesrSavetimeout)
    reviewesrSavetimeout = setTimeout(async () => {
      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && params.siteHistoryId) {
        await requestUpdateEditOnlySite(site.id, {
          reviewersContactsThatCanComment: e.target.value,
          proposalPurpose: 8
        })
      } else {
        await requestUpdateSite(params.id, historyId, { reviewersContactsThatCanComment: e.target.value })
      }
    }, 300)
  }

  const applyProposalPurposeChange = async (selectedPurpose) => {
    setProposalPurpose(selectedPurpose)
    await requestUpdateSite(params.id, historyId, { proposalPurpose: selectedPurpose.value })
  }

  const handleProposalPurposeChange = async (selectedPurpose) => {
    if (
      shouldShowSensitiveWarning(
        selectedPurpose.value,
        isIndividualProposer,
        hasHiddenSensitiveForCurrentUser,
        isNewUserWithoutAccess
      )
    ) {
      setPendingProposalPurpose(selectedPurpose)
      setIsSensitiveWarningOpen(true)
      return
    }

    await applyProposalPurposeChange(selectedPurpose)
  }

  const handleSensitiveWarningConfirm = async () => {
    if (isConfirmingWarning) {
      return
    }
    setIsConfirmingWarning(true)

    try {
      if (pendingProposalPurpose) {
        await applyProposalPurposeChange(pendingProposalPurpose)
        setPendingProposalPurpose(null)
      }

      const ackResponse =
        typeof requestSubmitSensitiveSpeciesAcknowledgment === 'function'
          ? await requestSubmitSensitiveSpeciesAcknowledgment(params.id, historyId)
          : null

      // Check if backend indicates this was already submitted
      const alreadySubmitted =
        ackResponse?.alreadySubmitted === true ||
        ackResponse?.already_submitted === true ||
        (typeof ackResponse?.message === 'string' && ackResponse.message.toLowerCase().includes('already'))

      if (getMySite) {
        await getMySite()
      }
      if (getSiteValidation) {
        await getSiteValidation()
      }
      const sensitiveEventDetail = { siteId: params.id, siteHistoryId: historyId }
      window.dispatchEvent(new CustomEvent('sensitiveSpeciesAcknowledged', { detail: sensitiveEventDetail }))
      window.dispatchEvent(new CustomEvent('sensitiveSpeciesDataChanged', { detail: sensitiveEventDetail }))

      if (alreadySubmitted) {
        showSuccessToast(
          <FormattedMessage
            id="sensitiveSpeciesAlreadySubmitted"
            defaultMessage="This has already been submitted."
          />
        )
      } else {
        showSuccessToast(
          <FormattedMessage
            id="sensitiveSpeciesSubmitted"
            defaultMessage="Your request to reassess this site has been received and will be processed shortly. Thank you for your patience."
          />
        )
      }

      setIsSensitiveWarningOpen(false)
    } catch (error) {
      const message = typeof error?.message === 'string' ? error.message.toLowerCase() : ''
      const alreadySubmitted = message.includes('already')
      showErrorToast(
        alreadySubmitted ? (
          <FormattedMessage
            id="sensitiveSpeciesAlreadySubmitted"
            defaultMessage="This has already been submitted."
          />
        ) : (
          <FormattedMessage
            id="sensitiveSpeciesSubmitError"
            defaultMessage="Failed to submit sensitive species data. Please try again."
          />
        )
      )
    } finally {
      setIsConfirmingWarning(false)
    }
  }

  const handleSensitiveWarningClose = () => {
    setPendingProposalPurpose(null)
    setIsSensitiveWarningOpen(false)
  }

  const handleProposalDetailsChange = async (e) => {
    setProposalDetails(e.target.value)
    if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && params.siteHistoryId) {
      await requestUpdateEditOnlySite(site.id, { proposalDetails: e.target.value, proposalPurpose: 8 })
    } else {
      await requestUpdateSite(params.id, historyId, { proposalDetails: e.target.value })
    }
  }

  // Filter out edit-only options when site status is draft
  const filteredProposalPurposeOptions =
    site.SiteStatusId === SITE_STATUS_DRAFT_ID
      ? proposalPurposeOptions.filter((option) => option.value !== 7 && option.value !== 8)
      : proposalPurposeOptions

  return (
    <div>
      <SensitiveSpeciesWarningModal
        isOpen={isSensitiveWarningOpen}
        onConfirm={handleSensitiveWarningConfirm}
        onClose={handleSensitiveWarningClose}
        isLoading={isConfirmingWarning}
      />
      <PageTitle data-test="legend">
        <FormattedMessage id="aboutPageTitle" />
      </PageTitle>
      <PageSubtitleWrapper>
        <PageSubtitle data-test="sub-legend">
          <FormattedMessage id="aboutPageSubTitle" />
        </PageSubtitle>
        <Tooltip
          style={{ zIndex: 1000 }}
          placement="bottom"
          trigger={['hover']}
          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
          overlay={{ label: 'aboutPageTitle', infoTooltip: 'aboutProposalHint' }}
          type="big"
        >
          <TooltipHintWrapper>
            <InfoIcon />
          </TooltipHintWrapper>
        </Tooltip>
      </PageSubtitleWrapper>
      <Container>
        <Row>
          <Col md={8} mdOffset={2}>
            <Select
              value={proposalPurpose}
              onChange={handleProposalPurposeChange}
              placeholder="proposalPurposePlaceholder"
              label="proposalPurposeLabel"
              field="proposalPurpose"
              infoTooltip="proposalPurposeHint"
              options={filteredProposalPurposeOptions}
              hyper={'https://www.keybiodiversityareas.org/working-with-kbas/proposing-updating/criteria-tools'}
              disabled={disabledBtn}
              error={
                siteProposalFieldValidation.about.proposalPurpose
                  ? { field: '', error: '' }
                  : {
                      field: 'proposalPurpose',
                      error: 'pleaseCompleteThisField'
                    }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="proposalDetailsLabel"
              value={proposalDetails}
              field="proposalDetails"
              onChange={handleProposalDetailsChange}
              placeholder="proposalDetailsPlaceholder"
              infoTooltip="proposalDetailsHint"
              height="128px"
              dataTest="proposal-details-text-area"
              isOptional={false}
              disabled={disabled}
              error={
                siteProposalFieldValidation.about.proposalDetails
                  ? { field: '', error: '' }
                  : {
                      field: 'proposalDetails',
                      error: 'pleaseCompleteThisField'
                    }
              }
            />
          </Col>
        </Row>

        {/* Existing fields */}
        <Row>
          <Col md={8} mdOffset={2}>
            <TwoStepMultiSelectDropdown
              marginBottom="8px"
              label={TwoStepMultiSelectLabel({
                heroText: 'A',
                labelText: 'threatenedBiodiversity',
                active: threatenedBiodiversity
              })}
              secondStepOptions={[
                { value: 1, label: 'A1 Threatened Species' },
                { value: 2, label: 'A2 Threatened Ecosystem Types' }
              ]}
              value={threatenedBiodiversity}
              saveChanges={updateThreatenedBiodiversity}
              disabled={disabledBtn}
            />
            <TwoStepMultiSelectDropdown
              marginBottom="8px"
              label={TwoStepMultiSelectLabel({
                heroText: 'B',
                labelText: 'geographicallyRestrictedBiodiversity',
                active: geographicallyRestrictedBiodiversity
              })}
              secondStepOptions={[
                { value: 1, label: 'B1 Individual geographically restricted species' },
                { value: 2, label: 'B2 Co-occuring geographically restricted species' },
                { value: 3, label: 'B3 Geographically restricted assemblages' },
                { value: 4, label: 'B4 Geographically restricted ecosystem types' }
              ]}
              value={geographicallyRestrictedBiodiversity}
              saveChanges={updateGeographicallyRestrictedBiodiversity}
              disabled={disabledBtn}
            />

            <TwoStepMultiSelectDropdown
              marginBottom="8px"
              label={TwoStepMultiSelectLabel({
                heroText: 'C',
                labelText: 'ecologicalIntegrity',
                active: ecologicalIntegrity
              })}
              value={ecologicalIntegrity}
              saveChanges={updateEcologicalIntegrity}
              disabled={disabledBtn}
            />
            <TwoStepMultiSelectDropdown
              marginBottom="8px"
              label={TwoStepMultiSelectLabel({
                heroText: 'D',
                labelText: 'biologicalProcesses',
                active: biologicalProcesses
              })}
              secondStepOptions={[
                { value: 1, label: 'D1 Demographic aggregations' },
                { value: 2, label: 'D2 Ecological refugia' },
                { value: 3, label: 'D3 Recruitment sources' }
              ]}
              value={biologicalProcesses}
              saveChanges={updateBiologicalProcesses}
              disabled={disabledBtn}
            />
            <TwoStepMultiSelectDropdown
              marginBottom="96px"
              label={TwoStepMultiSelectLabel({
                heroText: 'E',
                labelText: 'irreplaceabilityThroughQuantitiveAnalysis',
                active: irreplaceabilityThroughQuantitiveAnalysis
              })}
              value={irreplaceabilityThroughQuantitiveAnalysis}
              saveChanges={updateIrreplaceabilityThroughQuantitiveAnalysis}
              disabled={disabledBtn}
            />
          </Col>
        </Row>

        <Row>
          <Col md={8} mdOffset={2.0}>
            <UploadSiteFiles
              label="uploadSiteFiles"
              field="uploadSiteFiles"
              infoTooltip="uploadSiteFilesHint"
              height="128px"
              dataTest="uploadSiteFiles-text-area"
              isOptional={true}
              disabled={disabledBtn}
            />
            <Uploader
              params={params}
              siteFiles={(site.siteFiles || []).filter((file) => {
                const fileUrl = file.fileUrl || file.url || file.sasUrl || file.download_url || ''
                const blobName = file.blob_name || file.blobName || ''
                return !fileUrl.includes('/criterion-c/') && !blobName.includes('criterion-c')
              })}
              disabled={disabledBtn}
              uploadFn={async (uploadSiteId, uploadSiteHistoryId, formData) => {
                return await requestUpdateSiteFiles(uploadSiteId, uploadSiteHistoryId, formData)
              }}
              deleteFn={async (deleteSiteId, deleteSiteHistoryId, fileId) => {
                return await requestDeleteSiteFiles(deleteSiteId, deleteSiteHistoryId, fileId)
              }}
              fetchFilesFn={async (fetchSiteId, fetchSiteHistoryId) => {
                const response = await requestGetSiteFiles(fetchSiteId, fetchSiteHistoryId)
                // Ensure we only return general site files, not criterion C files
                if (Array.isArray(response)) {
                  return response.filter((file) => {
                    const fileUrl = file.fileUrl || file.url || file.sasUrl || file.download_url || ''
                    const blobName = file.blob_name || file.blobName || ''
                    return !fileUrl.includes('/criterion-c/') && !blobName.includes('criterion-c')
                  })
                }
                if (response?.files) {
                  return {
                    ...response,
                    files: response.files.filter((file) => {
                      const fileUrl = file.fileUrl || file.url || file.sasUrl || file.download_url || ''
                      const blobName = file.blob_name || file.blobName || ''
                      return !fileUrl.includes('/criterion-c/') && !blobName.includes('criterion-c')
                    })
                  }
                }
                return response
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="biodiversityKnowledgeHolders"
              value={biodiversityKnowledgeHolders}
              field="biodiversityKnowledgeHolders"
              infoTooltip="biodiversityKnowledgeHoldersHint"
              onChange={handleChangeBiodiversityKnowledgeHolders}
              placeholder="biodiversityKnowledgeHoldersPlaceholder"
              height="128px"
              dataTest="biodiversityKnowledgeHolders-text-area"
              isOptional={false}
              disabled={disabled}
              error={
                siteProposalFieldValidation.about.consultationWithBiodiversityKnowledgeHolders
                  ? { field: '', error: '' }
                  : {
                      field: 'biodiversityKnowledgeHolders',
                      error: 'pleaseCompleteThisField'
                    }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="previousProposers"
              value={previousProposers}
              field="previousProposers"
              infoTooltip="previousProposersHint"
              onChange={handleChangePreviousProposers}
              placeholder="previousProposersPlaceholder"
              height="128px"
              dataTest="previousProposers-text-area"
              isOptional={false}
              disabled={disabled}
              error={
                siteProposalFieldValidation.about.consultationWithPreviousProposers
                  ? { field: '', error: '' }
                  : {
                      field: 'previousProposers',
                      error: 'pleaseCompleteThisField'
                    }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="government"
              value={government}
              field="government"
              infoTooltip="governmentHint"
              onChange={handleChangeGovernment}
              placeholder="governmentPlaceholder"
              height="128px"
              dataTest="government-text-area"
              isOptional={false}
              disabled={disabled}
              error={
                siteProposalFieldValidation.about.consultationWithGovernment
                  ? { field: '', error: '' }
                  : {
                      field: 'government',
                      error: 'pleaseCompleteThisField'
                    }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="customaryLegalRightsHolders"
              value={customaryLegalRightsHolders}
              field="customaryLegalRightsHolders"
              infoTooltip="customaryLegalRightsHoldersHint"
              onChange={handleChangeCustomaryLegalRightsHolders}
              placeholder="customaryLegalRightsHoldersPlaceholder"
              height="128px"
              dataTest="customaryLegalRightsHolders-text-area"
              isOptional={false}
              disabled={disabled}
              error={
                siteProposalFieldValidation.about.consultationWithCustomaryLegalRightsHolders
                  ? { field: '', error: '' }
                  : {
                      field: 'customaryLegalRightsHolders',
                      error: 'pleaseCompleteThisField'
                    }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="contributors_assessment"
              value={contributors_assessment}
              field="contributors_assessment"
              infoTooltip="contributorsAssessmentHint"
              onChange={handleChangeContributorsAssessment}
              placeholder="contributorsAssessmentPlaceholder"
              height="128px"
              dataTest="contributors_assessment-text-area"
              isOptional={false}
              disabled={disabled}
              error={
                siteProposalFieldValidation.about.contributorsAssessment
                  ? { field: '', error: '' }
                  : {
                      field: 'contributors_assessment',
                      error: 'pleaseCompleteThisField'
                    }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={8} mdOffset={2}>
            <Textarea
              label="reviewers"
              value={reviewers}
              field="reviewersContactsThatCanComment"
              onChange={onReviewersChange}
              placeholder="provideReviewersInfoWhoCanCommentOnYourProposal"
              height="128px"
              dataTest="reviewers-text-area"
              isOptional={true}
              disabled={disabled}
              marginBottom="0"
            />
          </Col>
        </Row>
        <ScrollToTop bottom="104px" />
      </Container>
    </div>
  )
}

export default SiteProposalAboutPage
export { parseSensitiveFlag, SPECIES_SENSITIVE_KEYS, isSpeciesSensitive } from 'utils/sensitiveSpecies'
export { shouldShowSensitiveWarning }

